
import React, { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "./types";

interface RelatedDataCounts {
  contacts: number;
  documents: number;
  interactions: number;
  newsAlerts: number;
  enrichmentResults: number;
  jobs: number;
  revenue: number;
  employmentHistory: number;
}

interface EnhancedClientDeletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  client: Client;
}

export const EnhancedClientDeletionDialog: React.FC<EnhancedClientDeletionDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  client
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [relatedData, setRelatedData] = useState<RelatedDataCounts | null>(null);

  useEffect(() => {
    if (isOpen && client) {
      fetchRelatedDataCounts();
    }
  }, [isOpen, client]);

  const fetchRelatedDataCounts = async () => {
    setIsLoading(true);
    try {
      // Fetch counts for all related tables
      const [
        contactsResult,
        documentsResult,
        interactionsResult,
        newsAlertsResult,
        enrichmentResult,
        jobsResult,
        revenueResult,
        employmentResult
      ] = await Promise.all([
        supabase.from('client_contacts').select('id', { count: 'exact', head: true }).eq('client_id', client.id),
        supabase.from('client_documents').select('id', { count: 'exact', head: true }).eq('client_id', client.id),
        supabase.from('client_contact_interactions').select('id', { count: 'exact', head: true }).eq('client_id', client.id),
        supabase.from('client_news_alerts').select('id', { count: 'exact', head: true }).eq('client_id', client.id),
        supabase.from('client_enrichment_results').select('id', { count: 'exact', head: true }).eq('client_id', client.id),
        supabase.from('jobs_clients').select('id', { count: 'exact', head: true }).eq('client_id', client.id),
        supabase.from('user_revenue').select('id', { count: 'exact', head: true }).eq('client_id', client.id),
        supabase.from('candidate_employment_history').select('id', { count: 'exact', head: true }).eq('client_id', client.id)
      ]);

      setRelatedData({
        contacts: contactsResult.count || 0,
        documents: documentsResult.count || 0,
        interactions: interactionsResult.count || 0,
        newsAlerts: newsAlertsResult.count || 0,
        enrichmentResults: enrichmentResult.count || 0,
        jobs: jobsResult.count || 0,
        revenue: revenueResult.count || 0,
        employmentHistory: employmentResult.count || 0
      });
    } catch (error) {
      console.error('Error fetching related data counts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDeletion = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error during deletion:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getTotalRelatedRecords = () => {
    if (!relatedData) return 0;
    return Object.values(relatedData).reduce((sum, count) => sum + count, 0);
  };

  const getDataBreakdown = () => {
    if (!relatedData) return [];
    
    const breakdown = [];
    if (relatedData.contacts > 0) breakdown.push(`${relatedData.contacts} contact${relatedData.contacts !== 1 ? 's' : ''}`);
    if (relatedData.documents > 0) breakdown.push(`${relatedData.documents} document${relatedData.documents !== 1 ? 's' : ''}`);
    if (relatedData.interactions > 0) breakdown.push(`${relatedData.interactions} interaction${relatedData.interactions !== 1 ? 's' : ''}`);
    if (relatedData.newsAlerts > 0) breakdown.push(`${relatedData.newsAlerts} news alert${relatedData.newsAlerts !== 1 ? 's' : ''}`);
    if (relatedData.enrichmentResults > 0) breakdown.push(`${relatedData.enrichmentResults} enrichment result${relatedData.enrichmentResults !== 1 ? 's' : ''}`);
    if (relatedData.jobs > 0) breakdown.push(`${relatedData.jobs} job association${relatedData.jobs !== 1 ? 's' : ''}`);
    if (relatedData.revenue > 0) breakdown.push(`${relatedData.revenue} revenue record${relatedData.revenue !== 1 ? 's' : ''}`);
    if (relatedData.employmentHistory > 0) breakdown.push(`${relatedData.employmentHistory} employment record${relatedData.employmentHistory !== 1 ? 's' : ''}`);
    
    return breakdown;
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Client "{client?.name}"?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. This will permanently delete the client and all associated data.
              </p>
              
              {isLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Analyzing related data...</span>
                </div>
              ) : relatedData ? (
                <div className="space-y-3">
                  <div className="p-3 bg-muted rounded-lg">
                    <h4 className="font-medium text-sm mb-2">
                      What will be deleted:
                    </h4>
                    
                    {getTotalRelatedRecords() === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No related data found. Only the client record will be deleted.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive" className="text-xs">
                            {getTotalRelatedRecords()} related records
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {getDataBreakdown().join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-orange-800">
                      <strong>Warning:</strong> This action is permanent and cannot be reversed. 
                      All data associated with this client will be lost forever.
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirmDeletion} 
            disabled={isLoading || isDeleting}
            className="min-w-[100px]"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Client
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
